import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Type, 
  Volume2, 
  VolumeX, 
  Palette, 
  RotateCcw,
  HelpCircle,
  ChevronUp,
  ChevronDown,
  Trophy,
  ChevronRight
} from 'lucide-react';
import { useGame } from '../context/GameContext';
import { useAuth } from '../hooks/useAuth';
import { isUserAdmin } from '../utils/admin';

interface ModernGameControlsProps {
  onShowSettings: () => void;
  onShowInstructions: () => void;
  onShowCompletion?: () => void;
}

const ModernGameControls: React.FC<ModernGameControlsProps> = ({ 
  onShowSettings, 
  onShowInstructions,
  onShowCompletion
}) => {
  const { gameState, actions } = useGame();
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const isAdmin = isUserAdmin(user);

  const fontSizeOptions = [
    { size: 0.7, label: 'XS', icon: 'A' },
    { size: 0.85, label: 'S', icon: 'A' },
    { size: 1.0, label: 'M', icon: 'A' },
    { size: 1.1, label: 'L', icon: 'A' },
    { size: 1.2, label: 'XL', icon: 'A' },
  ];

  return (
    <div className="fixed bottom-6 right-6 z-10 flex gap-4 items-end">
      {/* Performance/Results Button - Only shown when game is completed */}
      <AnimatePresence>
        {gameState.gameCompleted && onShowCompletion && (
          <motion.button
            onClick={onShowCompletion}
            className="w-12 h-12 rounded-full bg-green-600 hover:bg-green-700 shadow-lg flex items-center justify-center text-white"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            initial={{ opacity: 0, scale: 0, x: 20 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0, x: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <ChevronRight className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Settings Button with Expandable Panel */}
      <motion.div
        className="relative"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.5 }}
      >
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              className="absolute bottom-16 right-0 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-200/50 p-4 min-w-72"
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <Type className="w-4 h-4" />
                    <span>Text Size</span>
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    {fontSizeOptions.map((option) => (
                      <motion.button
                        key={option.size}
                        onClick={() => actions.setFontSize(option.size)}
                        className={`h-10 rounded-xl flex flex-col items-center justify-center text-xs font-bold transition-all ${
                          gameState.fontSizeScale === option.size
                            ? 'bg-gray-900 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <span>{option.icon}</span>
                        <span className="text-xs opacity-75">{option.label}</span>
                      </motion.button>
                    ))}
                  </div>
                </div>

                <div className={`grid gap-3 ${isAdmin ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  <motion.button
                    onClick={onShowInstructions}
                    className="flex items-center gap-2 p-3 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors text-slate-700"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <HelpCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Help</span>
                  </motion.button>

                  {isAdmin && (
                    <motion.button
                      onClick={actions.resetGame}
                      className="flex items-center gap-2 p-3 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition-colors"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <RotateCcw className="w-4 h-4" />
                      <span className="text-sm font-medium">Reset</span>
                    </motion.button>
                  )}
                </div>

                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full" />
                    <span className="text-sm font-medium text-slate-700">Confirm Moves</span>
                  </div>
                  <motion.button
                    onClick={() => actions.setConfirmMode(!gameState.confirmMode)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      gameState.confirmMode ? 'bg-gray-900' : 'bg-slate-300'
                    }`}
                    whileTap={{ scale: 0.95 }}
                  >
                    <motion.div
                      className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm"
                      animate={{ 
                        x: gameState.confirmMode ? 24 : 0 
                      }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white transition-all duration-300 ${
            isExpanded 
              ? 'bg-red-600 hover:bg-red-700' 
              : 'bg-gray-900 hover:bg-gray-800'
          }`}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          animate={{ 
            rotate: isExpanded ? 45 : 0,
          }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
        >
          {isExpanded ? (
            <motion.div
              initial={{ rotate: 0 }}
              animate={{ rotate: 45 }}
            >
              ✕
            </motion.div>
          ) : (
            <ChevronUp className="w-6 h-6" />
          )}
        </motion.button>
      </motion.div>
    </div>
  );
};

export default ModernGameControls;